import React, { useCallback, useEffect, useRef, useState } from 'react';
import PaginatedTable from '../PaginatedTable';
import CsvButton from '../CsvButton';
import { ErrorState } from '../../shared/StateMessage';
import { sanitizePanelError } from '../../shared/PanelErrorBoundary';
import { getInspection, getSitemaps } from '../../../api';
import { verdictColor } from '../lib/colors';
import StatTile from '../StatTile';
import { fmtCount, fmtRelativeTime, fmtDate, pagePath, fmtEnum } from '../lib/format';

/**
 * Evidence for `indexing_health` and `sitemap_health`.
 *
 * Both panels are INFORMATIONAL (F12). A failed inspection is not an outage: it means
 * Google has not confirmed it can show that page yet. So there is no red banner, no
 * alert styling, and the page has no alert panel at all — verdict colour is a small
 * dot next to a word, and the word carries the meaning on its own.
 *
 * Vocabulary (F19): "indexed" means Google confirmed it CAN SHOW the page. It is not a
 * ranking claim, and nothing here phrases it as one.
 *
 * ── Paging (2026-08-14 backend addendum §2) ────────────────────────────────────
 * Both panels now have paginated RPCs — `fn_get_gsc_url_inspection` and
 * `fn_get_gsc_sitemaps` — already granted to authenticated. Neither takes a date range;
 * both are current-state, not time series. Same {summary, items, pagination} shape as
 * the three Detail RPCs. The panel payload carries page 1 (25 rows); page changes call
 * the RPC directly, exactly like `DetailTable`.
 *
 * ── Column selection ────────────────────────────────────────────────────────────
 * An inspection row carries 15 fields and a sitemap row 13; neither fits a card layout
 * at 640px. Four columns are shown by default and the rest sit behind row expansion.
 *
 * The spec asks for a column to be dropped when every row's value is null — Google
 * deprecated the `indexed_*` counts and returns them null, and a column of blanks reads
 * as missing data rather than as a discontinued metric. Because the extra fields render
 * as a per-row definition list rather than as table columns, `PaginatedTable` already
 * omits any field with no value FOR THAT ROW, which delivers the same result and also
 * handles the mixed case the column rule cannot: a field Google returns for some pages
 * and not others. A separate all-null column filter on top of that could never change
 * what is on screen, so there isn't one.
 */

const VerdictChip = ({ verdict }) => (
  <span className="inline-flex items-center gap-1.5">
    <span
      aria-hidden="true"
      className="h-2 w-2 shrink-0 rounded-full"
      style={{ backgroundColor: verdictColor(verdict) }}
    />
    <span>{verdict || 'Not checked'}</span>
  </span>
);

const indexingColumns = () => [
  {
    key: 'inspected_url',
    label: 'Page',
    // The page is what the row is ABOUT; the verdict, status and crawl time beside it are
    // its detail. Weight and the brand colour say which is which, so the eye lands on the
    // path first when scanning a table of 25. The full URL stays on `title` — the cell
    // shows the path, because an owner does not read hostnames they already know.
    render: (r) => (
      <span className="font-semibold text-[#32B1FF]">{pagePath(r.inspected_url)}</span>
    ),
    title: (r) => r.inspected_url,
  },
  { key: 'verdict', label: 'Result', render: (r) => <VerdictChip verdict={r.verdict} /> },
  { key: 'coverage_state', label: 'Status in Google', render: (r) => r.coverage_state || '' },
  { key: 'last_crawl_time', label: 'Last checked', render: (r) => fmtRelativeTime(r.last_crawl_time) },
];

const INDEXING_EXTRA = [
  { key: 'robots_txt_state', label: 'Robots.txt', render: (r) => fmtEnum(r.robots_txt_state) },
  { key: 'indexing_state', label: 'Indexing', render: (r) => fmtEnum(r.indexing_state) },
  { key: 'page_fetch_state', label: 'Fetch', render: (r) => fmtEnum(r.page_fetch_state) },
  { key: 'crawled_as', label: 'Crawled as', render: (r) => fmtEnum(r.crawled_as) },
  { key: 'google_canonical', label: "Google's chosen address", render: (r) => r.google_canonical },
  { key: 'user_canonical', label: 'Your chosen address', render: (r) => r.user_canonical },
  { key: 'canonical_mismatch', label: 'Addresses differ', render: (r) => (r.canonical_mismatch === true ? 'Yes' : r.canonical_mismatch === false ? 'No' : '') },
  { key: 'rich_results_verdict', label: 'Rich results', render: (r) => fmtEnum(r.rich_results_verdict) },
  { key: 'rich_results_types', label: 'Rich result types', render: (r) => (Array.isArray(r.rich_results_types) ? r.rich_results_types.join(', ') : '') },
  { key: 'mobile_usability_verdict', label: 'Mobile usability', render: (r) => r.mobile_usability_verdict },
  { key: 'inspected_at', label: 'Inspected', render: (r) => fmtRelativeTime(r.inspected_at) },
];

/**
 * When Google last read a sitemap, as a date, with a calm marker when the backend says
 * the read is stale.
 *
 * The date is `fmtDate(last_downloaded)`, not a day count: an owner reads "Sep 4, 2019"
 * faster than "2,590 days". Staleness is the backend's call (`is_stale`, true when Google
 * has not read the sitemap in over 30 days, or never has) and is never recomputed from
 * the date here, so the threshold lives in one place. The marker is informational — a
 * neutral dot and a word, no alert colour — like the rest of the sitemap panel.
 *
 * A stale row with no date reads "Not read yet" (Google has never read it); a row with no
 * date that is not stale renders nothing, as the other columns do for a null.
 */
const SitemapLastRead = ({ row }) => {
  const date = fmtDate(row.last_downloaded);
  const stale = row.is_stale === true;
  if (!date && !stale) return null;
  return (
    <span className="inline-flex flex-col">
      {date && <span>{date}</span>}
      {stale && (
        <span data-sitemap-stale className="inline-flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400">
          <span aria-hidden="true" className="h-2 w-2 shrink-0 rounded-full bg-gray-400 dark:bg-gray-500" />
          {date ? 'Not read recently' : 'Not read yet'}
        </span>
      )}
    </span>
  );
};

const sitemapColumns = () => [
  { key: 'path', label: 'Sitemap', render: (r) => pagePath(r.path), title: (r) => r.path },
  { key: 'submitted_web', label: 'Pages submitted', align: 'right', render: (r) => fmtCount(r.submitted_web) },
  { key: 'errors', label: 'Errors', align: 'right', render: (r) => fmtCount(r.errors) },
  { key: 'warnings', label: 'Warnings', align: 'right', render: (r) => fmtCount(r.warnings) },
  { key: 'last_downloaded', label: 'Last read by Google', render: (r) => <SitemapLastRead row={r} /> },
];

const SITEMAP_EXTRA = [
  { key: 'sitemap_type', label: 'Type', render: (r) => fmtEnum(r.sitemap_type) },
  { key: 'is_sitemaps_index', label: 'Index of other sitemaps', render: (r) => (r.is_sitemaps_index === true ? 'Yes' : r.is_sitemaps_index === false ? 'No' : '') },
  { key: 'is_pending', label: 'Waiting to be read', render: (r) => (r.is_pending === true ? 'Yes' : r.is_pending === false ? 'No' : '') },
  { key: 'last_submitted', label: 'Submitted', render: (r) => fmtRelativeTime(r.last_submitted) },
  { key: 'indexed_web', label: 'Pages indexed', render: (r) => fmtCount(r.indexed_web) },
  { key: 'submitted_image', label: 'Images submitted', render: (r) => fmtCount(r.submitted_image) },
  { key: 'submitted_video', label: 'Videos submitted', render: (r) => fmtCount(r.submitted_video) },
  { key: 'submitted_news', label: 'News items submitted', render: (r) => fmtCount(r.submitted_news) },
];

/** True when a value is renderable as a number, matching `fmtCount`'s own admission rule. */
const isCount = (v) => v !== null && v !== undefined && v !== '' && Number.isFinite(Number(v));

/**
 * Pages Google is not showing, derived from the two figures beside it rather than read
 * from `summary.not_indexed`.
 *
 * The backend's contract is `not_indexed = neutral + fail`, and it does not always hold.
 * Verde Ranch returns `pass: 59`, `neutral: 5`, `fail: 0` and `not_indexed: 4` against
 * `total_inspected: 64`, so the tiles read "64 checked, 59 shown, 4 not" and lose a page
 * in front of the reader — while `coverage_states` right underneath lists all five.
 *
 * Deriving closes the arithmetic by construction: the two outcome tiles always account
 * for every checked page, and they keep doing so if `fail` becomes non-zero or the field
 * is corrected upstream. Reading any single field leaves the page one bad value away
 * from the same defect.
 *
 * Returns '' when either input is missing or the subtraction would go negative, so
 * `StatTile` omits the tile rather than render a count that cannot be true.
 */
const notShownCount = (summary) => {
  if (!isCount(summary?.total_inspected) || !isCount(summary?.pass)) return '';
  const notShown = Number(summary.total_inspected) - Number(summary.pass);
  return notShown < 0 ? '' : fmtCount(notShown);
};

const IndexingSummary = ({ summary }) => {
  const states = summary?.coverage_states && typeof summary.coverage_states === 'object'
    ? Object.entries(summary.coverage_states)
    : [];
  return (
    <div className="space-y-3">
      <dl className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
        <StatTile label="Pages checked" value={fmtCount(summary?.total_inspected)} icon="checklist" tone="cyan" description="Pages Google has inspected on your site." />
        <StatTile label="Google can show" value={fmtCount(summary?.pass)} icon="eye" tone="violet" description="Indexed and eligible to appear in results." />
        <StatTile label="Google cannot show yet" value={notShownCount(summary)} icon="eyeOff" tone="fuchsia" description="Not indexed yet — still processing or blocked." />
        <StatTile label="Different address chosen" value={fmtCount(summary?.canonical_mismatch)} icon="share" tone="pink" description="Google indexed a different URL than the one you picked." />
      </dl>
      {states.length > 0 && (
        <ul className="flex flex-wrap gap-2 text-sm">
          {states.map(([state, count]) => (
            <li key={state} className="rounded-full border border-gray-200 dark:border-gray-700 bg-gray-100 dark:bg-gray-800 px-2.5 py-0.5 text-gray-700 dark:text-gray-300">
              {state}: {fmtCount(count)}
            </li>
          ))}
        </ul>
      )}
      {summary?.newest_crawl && (
        <p className="text-xs text-gray-400 dark:text-gray-500">
          Most recent check {fmtRelativeTime(summary.newest_crawl)}.
        </p>
      )}
    </div>
  );
};

/**
 * The sitemap roll-up. Errors / Warnings open the per-sitemap table (`onExpand`) when
 * there is something to show; Google's Sitemaps data has no URL-level detail, so the
 * table — one row per sitemap with its own error and warning counts — is the drill.
 */
const SitemapSummary = ({ summary, onExpand }) => (
  <div className="space-y-3">
    <dl className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
      <StatTile label="Sitemaps submitted" value={fmtCount(summary?.total_sitemaps)} icon="checklist" tone="cyan" description="Sitemap files you've submitted to Google." />
      <StatTile label="Pages listed" value={fmtCount(summary?.submitted_urls)} icon="share" tone="violet" description="URLs listed across those sitemaps." />
      <StatTile
        label="Errors"
        value={fmtCount(summary?.total_errors)}
        icon="bell"
        tone="fuchsia"
        description="Problems that stop Google reading a sitemap."
        onActivate={onExpand && Number(summary?.total_errors) > 0 ? onExpand : undefined}
        activateLabel="Errors — show which sitemaps have them"
      />
      <StatTile
        label="Warnings"
        value={fmtCount(summary?.total_warnings)}
        icon="flag"
        tone="pink"
        description="Minor issues that don't block indexing."
        onActivate={onExpand && Number(summary?.total_warnings) > 0 ? onExpand : undefined}
        activateLabel="Warnings — show which sitemaps have them"
      />
    </dl>
    {summary?.last_downloaded && (
      <p className="text-xs text-gray-400 dark:text-gray-500">
        Google last downloaded a sitemap on {fmtDate(summary.last_downloaded)}.
      </p>
    )}
  </div>
);

const FALLBACK_PAGE_LIMIT = 25;

/** Verdict values `fn_get_gsc_url_inspection` accepts for `p_verdict` (addendum §2). */
const VERDICTS = ['PASS', 'FAIL', 'NEUTRAL'];

/** Raw payload values for the spreadsheet — see `lib/csv.js`. */
const INDEXING_CSV = [
  { header: 'Page', get: (r) => r.inspected_url },
  { header: 'Result', get: (r) => fmtEnum(r.verdict) },
  { header: 'Coverage state', get: (r) => r.coverage_state },
  { header: 'Robots.txt state', get: (r) => fmtEnum(r.robots_txt_state) },
  { header: 'Indexing state', get: (r) => fmtEnum(r.indexing_state) },
  { header: 'Page fetch state', get: (r) => fmtEnum(r.page_fetch_state) },
  { header: 'Last crawl time', get: (r) => r.last_crawl_time },
  { header: 'Crawled as', get: (r) => fmtEnum(r.crawled_as) },
  { header: 'Google canonical', get: (r) => r.google_canonical },
  { header: 'User canonical', get: (r) => r.user_canonical },
  { header: 'Canonical mismatch', get: (r) => r.canonical_mismatch },
  { header: 'Rich results verdict', get: (r) => fmtEnum(r.rich_results_verdict) },
  { header: 'Inspected at', get: (r) => r.inspected_at },
];

const SITEMAP_CSV = [
  { header: 'Sitemap', get: (r) => r.path },
  { header: 'Type', get: (r) => fmtEnum(r.sitemap_type) },
  { header: 'Is index of other sitemaps', get: (r) => r.is_sitemaps_index },
  { header: 'Pending', get: (r) => r.is_pending },
  { header: 'Last submitted', get: (r) => r.last_submitted },
  { header: 'Last downloaded', get: (r) => r.last_downloaded },
  { header: 'Submitted web URLs', get: (r) => r.submitted_web },
  { header: 'Errors', get: (r) => r.errors },
  { header: 'Warnings', get: (r) => r.warnings },
];

const HealthSummary = ({ panel, facts, sentence, clientId, collapsed = true, onExpand }) => {
  const isIndexing = panel === 'indexing_health';

  const embedded = {
    items: Array.isArray(facts?.items) ? facts.items : [],
    pagination: facts?.pagination || null,
    summary: facts?.summary || null,
  };
  const pageLimit = embedded.pagination?.limit ?? FALLBACK_PAGE_LIMIT;

  const [live, setLive] = useState(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  // §7: "Full URL table, verdict filter". `p_verdict` is a server-side filter, so
  // choosing one re-queries rather than hiding rows from the page already fetched —
  // filtering client-side would silently narrow to whatever page 1 happened to hold.
  const [verdict, setVerdict] = useState(null);

  const fetchLive = useCallback(async (targetPage, targetVerdict) => {
    if (isIndexing) {
      return getInspection(clientId, { page: targetPage, limit: pageLimit, verdict: targetVerdict ?? null });
    }
    return getSitemaps(clientId, { page: targetPage, limit: pageLimit });
  }, [isIndexing, clientId, pageLimit]);

  const reqId = useRef(0);
  useEffect(() => () => { reqId.current += 1; }, []);

  const request = useCallback((targetPage, targetVerdict) => {
    if (!clientId) return;
    const id = reqId.current + 1;
    reqId.current = id;
    setLoading(true);
    setError('');
    fetchLive(targetPage, targetVerdict)
      .then((resp) => {
        if (reqId.current !== id) return;
        setLive(resp ?? { items: [], pagination: null, summary: null });
        setPage(targetPage);
      })
      .catch((e) => {
        // eslint-disable-next-line no-console
        console.error('[search-console] health RPC failed', e);
        if (reqId.current !== id) return;
        setError(sanitizePanelError(e?.message) || 'Something went wrong loading these rows.');
      })
      .finally(() => { if (reqId.current === id) setLoading(false); });
  }, [clientId, fetchLive]);

  const requestPage = useCallback((targetPage) => request(targetPage, verdict), [request, verdict]);

  /**
   * Apply or clear the verdict filter.
   *
   * Resets to page 1 in the same call rather than in an effect: page 7 of "everything"
   * is not page 7 of "only failures", and a filter that kept the page number would ask
   * the RPC for a page the filtered result set may not have.
   */
  const applyVerdict = (next) => {
    const value = verdict === next ? null : next;
    setVerdict(value);
    request(1, value);
  };

  const source = live || embedded;
  const rows = Array.isArray(source.items) ? source.items : [];
  const pagination = source.pagination || null;

  const columns = isIndexing ? indexingColumns() : sitemapColumns();
  const extras = isIndexing ? INDEXING_EXTRA : SITEMAP_EXTRA;

  return (
    <div className="space-y-3">
      {isIndexing
        ? <IndexingSummary summary={source.summary ?? facts?.summary} />
        : <SitemapSummary summary={source.summary ?? facts?.summary} onExpand={collapsed ? onExpand : undefined} />}

      {/* ── Expanded only: the full table and its controls (§7) ──────────────── */}
      {!collapsed && (
        <div className="space-y-3 border-t border-gray-200 dark:border-gray-800 pt-3">
          {isIndexing && (
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                Show
              </span>
              {VERDICTS.map((v) => {
                const active = verdict === v;
                const color = verdictColor(v);
                return (
                  <button
                    key={v}
                    type="button"
                    data-testid={`verdict-${v}`}
                    aria-pressed={active}
                    onClick={() => applyVerdict(v)}
                    // The outline is the chip's OWN status colour, echoing the dot inside
                    // it — a brand-blue border on the selected chip said "selected" but
                    // said nothing about which verdict was selected, and left the three
                    // chips indistinguishable at the edges. Inline because `VERDICT` is a
                    // hex map (shared with the table's dots), not a Tailwind scale; the
                    // trailing hex pair is the alpha.
                    style={{
                      borderColor: active ? color : `${color}59`,
                      backgroundColor: active ? `${color}1A` : undefined,
                    }}
                    // Selection is never colour alone: the chip also gains weight and a
                    // fill, and `aria-pressed` carries it for a screen reader.
                    className={`text-sm rounded-full px-3 py-1 border transition-colors text-gray-700 dark:text-gray-200 ${
                      active ? 'font-semibold' : 'hover:bg-gray-50 dark:hover:bg-gray-800/60'
                    }`}
                  >
                    <VerdictChip verdict={v} />
                  </button>
                );
              })}
              {verdict && (
                <button
                  type="button"
                  onClick={() => applyVerdict(verdict)}
                  className="text-sm text-gray-500 dark:text-gray-400 hover:underline"
                >
                  Clear
                </button>
              )}
            </div>
          )}

          {error && <ErrorState variant="inset" message={error} onRetry={() => requestPage(page)} />}

          <PaginatedTable
            columns={columns}
            rows={rows}
            rowKey={(r, i) => `${isIndexing ? r.inspected_url : r.path}-${i}`}
            pagination={pagination}
            onPageChange={requestPage}
            expandColumns={extras}
            loading={loading}
            label={isIndexing ? 'pages' : 'sitemaps'}
            ariaLabel={sentence}
            emptyMessage={isIndexing ? 'No pages match that result.' : 'No sitemaps submitted.'}
          />

          <CsvButton
            panelKey={panel}
            name={isIndexing ? 'Whether Google can see your pages' : 'Your sitemaps'}
            columns={isIndexing ? INDEXING_CSV : SITEMAP_CSV}
            rows={rows}
          />
        </div>
      )}
    </div>
  );
};

/**
 * Whether opening this card would change what is on screen (spec v2 §3).
 *
 * Collapsed is the summary tiles and the coverage-state chips; expanded is the full URL
 * or sitemap table, its verdict filter, its pager and the CSV. With no embedded rows
 * there is no table to open onto — the RPC could fetch some, but a control that has to
 * make a network call before it can prove it had anything to show is exactly the empty
 * expansion this guard exists to prevent.
 *
 * @param {object} facts the panel's `facts`
 * @returns {boolean} true when expansion reveals something
 */
HealthSummary.hasDepth = (facts) => Array.isArray(facts?.items) && facts.items.length > 0;


export default HealthSummary;
